import {inject, Injectable} from '@angular/core';
import {
  Customer,
  CustomerSource,
  CustomerVehicleRelation,
  CustomerWithVehicles,
  Vehicle
} from '@service-and-repairs/calm-leads-customer-lib-api';
import {OrderInfo, OrderSearchResult, VehicleInfo, VehicleSearchResult} from '@service-and-repairs/dms-api';
import {SearchCustomersCustomerReference, SearchCustomersVehicleReference} from '../models/customer';
import {SearchServiceOrdersResponse} from '../models/search-service-order';
import {SearchVehiclesResponse, VehicleCustomerRelationType} from '../models/vehicle';
import {AwpDmsInterfaceModelMapper} from './awp-dms-interface-model-mapper.service';

@Injectable({providedIn: 'root'})
export class AwpDmsInterfaceResponseMapper {
  private readonly converter: AwpDmsInterfaceModelMapper = inject(AwpDmsInterfaceModelMapper);

  mapVehicleSearchResponse(response: SearchVehiclesResponse, resultSize: number): VehicleSearchResult {
    const vehicleList = response.body?.vehicleList ?? [];
    const mapped: VehicleInfo[] = this.converter.mapVehicleResults(vehicleList);
    const limited = this.limit(mapped, resultSize);
    const maxResultSize = response.body?.resultSize ?? vehicleList.length;
    return new VehicleSearchResult(limited, maxResultSize);
  }

  mapOrderSearchResponse(response: SearchServiceOrdersResponse, resultSize: number): OrderSearchResult {
    const summaries = response.body?.serviceOrderList ?? [];
    const mapped: OrderInfo[] = this.converter.mapOrderSummariesToOrderInfos(summaries);
    const limited = this.limit(mapped, resultSize);
    const maxResultSize = response.body?.resultSize ?? summaries.length;
    return new OrderSearchResult(limited, maxResultSize);
  }

  mapSearchCustomersCustomerReferenceToCustomerWithVehicles(ref: SearchCustomersCustomerReference): CustomerWithVehicles {
    const customer: Customer = {
      source: CustomerSource.DMS,
      addressCollection: {
        privateAddress: {
          street: ref.street,
          streetNo: ref.streetNo,
          extraAddressLine1: undefined,
          extraAddressLine2: undefined,
          postalCode: ref.postCode,
          city: ref.city,
          state: undefined,
          country: undefined
        },
        businessAddress: undefined,
        mailingAddress: undefined,
        billingAddress: undefined,
        otherAddress: undefined
      },
      contactCollection: null,
      customerTypeCollection: null,
      idCollection: {dmsId: ref.dmsCustomerId, gcid: ref.gcdmCustomerId},
      nameCollection: {firstName: ref.firstName, lastName: ref.lastName, name: this.getFullName(ref)},
      isPreferred: false
    };

    const vehicles: Vehicle[] = (ref.vehicleReferenceList ?? []).map((v: SearchCustomersVehicleReference): any => ({
      vin: v.vin,
      brand: v.brand,
      numberPlate: v.numberPlate,
      modelDesignation: v.modelDesignation,
      relationType: this.mapVehicleRelationType(v.relationType)
    }));

    return {customer, vehicles};
  }

  private getFullName(ref: SearchCustomersCustomerReference): string {
    return [ref.firstName, ref.lastName]
      .filter((namePart: string | undefined): boolean => !!namePart)
      .join(' ');
  }

  private mapVehicleRelationType(relationType: VehicleCustomerRelationType): CustomerVehicleRelation {
    switch (relationType) {
      case 'REGISTERED_KEEPER':
        return CustomerVehicleRelation.DMS_REGISTERED_KEEPER;
      case 'USER':
        return CustomerVehicleRelation.DMS_USER;
      case 'PAYER_AFTERSALES':
        return CustomerVehicleRelation.DMS_PAYER_AFTERSALES;
      case 'PAYER_SALES':
        return CustomerVehicleRelation.DMS_PAYER_SALES;
      default:
        return CustomerVehicleRelation.UNKNOWN;
    }
  }

  private limit<T>(items: T[], resultSize?: number): T[] {
    if (!resultSize || resultSize >= items.length) {
      return items;
    }
    return items.slice(0, resultSize);
  }
}
